import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Clock, Timer, Calendar, Users, Plus, Search, Filter, ChevronDown, ChevronRight, CreditCard as Edit, Coffee, MapPin, ChevronLeft, Package, X, PenLine } from 'lucide-react';
import { getCurrentAESTDate, formatDisplayDate, getTodayAEST } from '../utils/dateUtils';
import { TimesheetForm } from '../components/forms/TimesheetForm';
import { Link } from 'react-router-dom';

interface Worker {
  id: string;
  name: string;
  role: string;
  email: string;
  status: string;
}

interface Site {
  id: string;
  name: string;
  address: string;
}

interface Break {
  id: string;
  break_type: string;
  duration_minutes: number;
  start_time: string;
  end_time: string;
}

interface Timesheet {
  id: string;
  worker_id: string;
  site_id: string | null;
  archived_site_id?: string | null;
  clock_in_time: string;
  clock_out_time: string | null;
  total_hours: number | null;
  status: string;
  clock_in_latitude: number | null;
  clock_in_longitude: number | null;
  clock_out_latitude: number | null;
  clock_out_longitude: number | null;
  hourly_rate_used?: number | null;
  overtime_rate_used?: number | null;
  regular_hours?: number | null;
  overtime_hours?: number | null;
  total_pay?: number | null;
  is_amended?: boolean;
  amended_at?: string | null;
  amended_note?: string | null;
  worker?: Worker;
  site?: Site | null;
  breaks?: Break[];
}

interface WorkerTimesheets {
  worker: Worker;
  weeks: WeekData[];
  totalHours: number;
}

interface SiteTimesheets {
  site: Site;
  weeks: WeekData[];
  totalHours: number;
}

interface WeekData {
  weekKey: string;
  weekStartDate: Date;
  weekEndDate: Date;
  timesheets: Timesheet[];
  grossHours: number;
  netHours: number;
  activeCount: number;
  completedCount: number;
}

// Day labels for the work week (Fri–Thu)
const DAY_LABELS = ['Fri', 'Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu'];

function formatDuration(hours: number): string {
  if (hours <= 0) return '0h';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function formatTime(isoString: string): string {
  return new Date(isoString).toLocaleTimeString('en-AU', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Australia/Sydney',
    hour12: false
  });
}

function getEffectiveHours(ts: Timesheet, now: number): number {
  if (ts.total_hours != null && ts.total_hours > 0) return ts.total_hours;
  if (!ts.clock_out_time && ts.clock_in_time) {
    return Math.max(0, (now - new Date(ts.clock_in_time).getTime()) / (1000 * 60 * 60));
  }
  return 0;
}

function getInitials(name: string): string {
  return name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
}

function Avatar({ name, size = 'sm' }: { name: string; size?: 'sm' | 'md' }) {
  const colors = [
    'bg-blue-100 text-blue-700',
    'bg-green-100 text-green-700',
    'bg-amber-100 text-amber-700',
    'bg-rose-100 text-rose-700',
    'bg-teal-100 text-teal-700',
    'bg-orange-100 text-orange-700',
  ];
  const idx = name.charCodeAt(0) % colors.length;
  const sizeClass = size === 'sm' ? 'h-8 w-8 text-xs' : 'h-10 w-10 text-sm';
  return (
    <div className={`${sizeClass} ${colors[idx]} rounded-full flex items-center justify-center font-semibold flex-shrink-0`}>
      {getInitials(name)}
    </div>
  );
}

export const TimesheetsPage: React.FC = () => {
  const [workerTimesheets, setWorkerTimesheets] = useState<WorkerTimesheets[]>([]);
  const [siteTimesheets, setSiteTimesheets] = useState<SiteTimesheets[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTimesheet, setEditingTimesheet] = useState<Timesheet | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [expandedWeeks, setExpandedWeeks] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterWorker, setFilterWorker] = useState('');
  const [filterSite, setFilterSite] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'worker' | 'site'>('grid');
  const [allWorkers, setAllWorkers] = useState<Worker[]>([]);
  const [allSites, setAllSites] = useState<Site[]>([]);
  const [allTimesheets, setAllTimesheets] = useState<Timesheet[]>([]);
  const [archivedSiteMap, setArchivedSiteMap] = useState<Map<string, { name: string; address: string }>>(new Map());

  // Week navigation state — offset from current week (0 = current)
  const [weekOffset, setWeekOffset] = useState(0);
  // Cell-level detail panel: show timesheets for a specific worker+day in the grid
  const [cellDetailPanel, setCellDetailPanel] = useState<{ workerId: string; workerName: string; dayKey: string } | null>(null);

  // Live clock for active timesheet durations — ticks every 30s
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  // Derive the Friday that starts the displayed week
  const currentWeekFriday = React.useMemo(() => {
    const today = getCurrentAESTDate();
    const dow = today.getDay(); // 0=Sun…6=Sat
    let daysBack: number;
    if (dow === 5) daysBack = 0;
    else if (dow === 6) daysBack = 1;
    else if (dow === 0) daysBack = 2;
    else if (dow === 1) daysBack = 3;
    else if (dow === 2) daysBack = 4;
    else if (dow === 3) daysBack = 5;
    else daysBack = 6; // Thursday
    const fri = new Date(today);
    fri.setDate(today.getDate() - daysBack);
    return fri;
  }, []);

  const displayedFriday = React.useMemo(() => {
    const d = new Date(currentWeekFriday);
    d.setDate(d.getDate() + weekOffset * 7);
    return d;
  }, [currentWeekFriday, weekOffset]);

  const days = React.useMemo(() =>
    Array.from({ length: 7 }, (_, i) => {
      const d = new Date(displayedFriday);
      d.setDate(displayedFriday.getDate() + i);
      return d;
    }), [displayedFriday]);

  const dayKeys = days.map(d =>
    new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Australia/Sydney',
      year: 'numeric', month: '2-digit', day: '2-digit'
    }).format(d)
  );

  const todayKey = React.useMemo(() => getTodayAEST(), []);

  const isCurrentWeek = weekOffset === 0;

  const weekLabel = React.useMemo(() => {
    const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    const thu = days[6];
    return `${displayedFriday.toLocaleDateString('en-AU', opts)} – ${thu.toLocaleDateString('en-AU', opts)} ${thu.getFullYear()}`;
  }, [days, displayedFriday]);

  // Build grid rows from all timesheets filtered to the displayed week
  const gridRows = React.useMemo(() => {
    const weekTs = allTimesheets.filter(ts => {
      const dateStr = new Date(ts.clock_in_time).toLocaleString('en-AU', {
        timeZone: 'Australia/Sydney',
        year: 'numeric', month: '2-digit', day: '2-digit'
      });
      const [d, m, y] = dateStr.split('/');
      const key = `${y}-${m}-${d}`;
      return dayKeys.includes(key);
    });

    // Group by worker
    const byWorker = new Map<string, Timesheet[]>();
    weekTs.forEach(ts => {
      if (!byWorker.has(ts.worker_id)) byWorker.set(ts.worker_id, []);
      byWorker.get(ts.worker_id)!.push(ts);
    });

    return Array.from(byWorker.entries()).map(([, entries]) => {
      const worker = entries[0].worker!;
      const dayMap: Record<string, { gross: number; net: number; breakTime: number; firstIn: string | null; lastOut: string | null; hasAmendment: boolean }> = {};
      dayKeys.forEach(k => { dayMap[k] = { gross: 0, net: 0, breakTime: 0, firstIn: null, lastOut: null, hasAmendment: false }; });

      entries.forEach(ts => {
        const dateStr = new Date(ts.clock_in_time).toLocaleString('en-AU', {
          timeZone: 'Australia/Sydney',
          year: 'numeric', month: '2-digit', day: '2-digit'
        });
        const [d, m, y] = dateStr.split('/');
        const key = `${y}-${m}-${d}`;
        if (!dayMap[key]) return;
        const gross = getEffectiveHours(ts, now);
        const breakMins = (ts.breaks || []).reduce((s, b) => s + (b.duration_minutes || 0), 0);
        const breakHrs = breakMins / 60;
        const net = Math.max(0, gross - breakHrs);
        dayMap[key].gross += gross;
        dayMap[key].breakTime += breakHrs;
        dayMap[key].net += net;
        if (ts.is_amended) dayMap[key].hasAmendment = true;
        if (!dayMap[key].firstIn || ts.clock_in_time < dayMap[key].firstIn!) {
          dayMap[key].firstIn = ts.clock_in_time;
        }
        if (ts.clock_out_time && (!dayMap[key].lastOut || ts.clock_out_time > dayMap[key].lastOut!)) {
          dayMap[key].lastOut = ts.clock_out_time;
        }
      });

      const weekNet = Object.values(dayMap).reduce((s, d) => s + d.net, 0);
      const weekGross = Object.values(dayMap).reduce((s, d) => s + d.gross, 0);
      const weekBreak = Object.values(dayMap).reduce((s, d) => s + d.breakTime, 0);

      return { profile: worker, days: dayMap, weekNet, weekGross, weekBreak };
    }).filter(r => {
      if (searchTerm) return r.profile.name.toLowerCase().includes(searchTerm.toLowerCase());
      return true;
    }).sort((a, b) => a.profile.name.localeCompare(b.profile.name));
  }, [allTimesheets, dayKeys, searchTerm, now]);

  const grandNet = gridRows.reduce((s, r) => s + r.weekNet, 0);
  const grandGross = gridRows.reduce((s, r) => s + r.weekGross, 0);
  const grandBreak = gridRows.reduce((s, r) => s + r.weekBreak, 0);

  const getTimesheetsForCell = (workerId: string, dayKey: string): Timesheet[] =>
    allTimesheets.filter(ts => {
      if (ts.worker_id !== workerId) return false;
      const dateStr = new Date(ts.clock_in_time).toLocaleString('en-AU', {
        timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit'
      });
      const [d, m, y] = dateStr.split('/');
      return `${y}-${m}-${d}` === dayKey;
    });

  const getWeekKey = (date: Date): string => {
    const aestDateString = date.toLocaleString('en-US', {
      timeZone: 'Australia/Sydney',
      year: 'numeric', month: '2-digit', day: '2-digit'
    });
    const [month, day, year] = aestDateString.split('/').map(Number);
    const aestDate = new Date(year, month - 1, day);
    const dayOfWeek = aestDate.getDay();
    let daysBack: number;
    if (dayOfWeek === 5) daysBack = 0;
    else if (dayOfWeek === 6) daysBack = 1;
    else if (dayOfWeek === 0) daysBack = 2;
    else if (dayOfWeek === 1) daysBack = 3;
    else if (dayOfWeek === 2) daysBack = 4;
    else if (dayOfWeek === 3) daysBack = 5;
    else daysBack = 6;
    const fridayStart = new Date(aestDate);
    fridayStart.setDate(aestDate.getDate() - daysBack);
    const yyyy = fridayStart.getFullYear();
    const mm = String(fridayStart.getMonth() + 1).padStart(2, '0');
    const dd = String(fridayStart.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const getWeekDates = (weekKey: string) => {
    const startDate = new Date(weekKey + 'T00:00:00');
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);
    return { startDate, endDate };
  };

  const fetchTimesheets = async () => {
    try {
      setLoading(true);
      const [timesheetsRes, archivedRes] = await Promise.all([
        supabase
          .from('timesheets')
          .select(`*, worker:workers(*), site:sites(*), breaks(*)`)
          .order('clock_in_time', { ascending: false }),
        supabase
          .from('archived_sites')
          .select('original_site_id, site_data'),
      ]);

      if (timesheetsRes.error) throw timesheetsRes.error;

      // Build a map of archived site id -> { name, address } for orphaned timesheets
      const aMap = new Map<string, { name: string; address: string }>();
      (archivedRes.data || []).forEach((a: any) => {
        const sd = a.site_data;
        if (sd && a.original_site_id) {
          aMap.set(a.original_site_id, { name: sd.name || 'Archived Site', address: sd.address || '' });
        }
      });
      setArchivedSiteMap(aMap);

      // Augment timesheets: if site is null but archived_site_id is set,
      // synthesize a site object from the archived site data
      const timesheetsWithDetails: Timesheet[] = (timesheetsRes.data || []).map((ts: any) => {
        if (!ts.site && ts.archived_site_id) {
          const archived = aMap.get(ts.archived_site_id);
          if (archived) {
            return { ...ts, site: { id: ts.archived_site_id, name: archived.name, address: archived.address } } as Timesheet;
          }
        }
        return ts as Timesheet;
      });

      setAllTimesheets(timesheetsWithDetails);

      // Group by worker
      const groupedByWorker = new Map<string, Timesheet[]>();
      timesheetsWithDetails.forEach(ts => {
        if (!groupedByWorker.has(ts.worker_id)) groupedByWorker.set(ts.worker_id, []);
        groupedByWorker.get(ts.worker_id)!.push(ts);
      });

      const workerData: WorkerTimesheets[] = Array.from(groupedByWorker.entries()).map(([, timesheets]) => {
        const worker = timesheets[0]?.worker;
        if (!worker) return null;
        const weeklyTimesheets = new Map<string, Timesheet[]>();
        timesheets.forEach(ts => {
          const weekKey = getWeekKey(new Date(ts.clock_in_time));
          if (!weeklyTimesheets.has(weekKey)) weeklyTimesheets.set(weekKey, []);
          weeklyTimesheets.get(weekKey)!.push(ts);
        });
        const weeks: WeekData[] = Array.from(weeklyTimesheets.entries()).map(([weekKey, weekTs]) => {
          const { startDate, endDate } = getWeekDates(weekKey);
          const grossHours = weekTs.reduce((s, ts) => s + getEffectiveHours(ts, now), 0);
          const netHours = weekTs.reduce((s, ts) => {
            const g = getEffectiveHours(ts, now);
            const bm = (ts.breaks || []).reduce((bs, b) => bs + (b.duration_minutes || 0), 0);
            return s + Math.max(0, g - bm / 60);
          }, 0);
          return {
            weekKey, weekStartDate: startDate, weekEndDate: endDate,
            timesheets: weekTs.sort((a, b) => new Date(b.clock_in_time).getTime() - new Date(a.clock_in_time).getTime()),
            grossHours, netHours,
            activeCount: weekTs.filter(ts => ts.status === 'active').length,
            completedCount: weekTs.filter(ts => ts.status === 'completed').length
          };
        });
        weeks.sort((a, b) => b.weekStartDate.getTime() - a.weekStartDate.getTime());
        return { worker, weeks, totalHours: weeks.reduce((s, w) => s + w.netHours, 0) };
      }).filter(Boolean) as WorkerTimesheets[];

      const uniqueWorkers = Array.from(new Set(workerData.map(w => w.worker))).sort((a, b) => a.name.localeCompare(b.name));
      setAllWorkers(uniqueWorkers);
      setWorkerTimesheets(workerData);

      // Group by site (use archived_site_id as fallback key when site_id is null)
      const groupedBySite = new Map<string, Timesheet[]>();
      timesheetsWithDetails.forEach(ts => {
        const key = ts.site_id ?? ts.archived_site_id ?? 'no-site';
        if (!groupedBySite.has(key)) groupedBySite.set(key, []);
        groupedBySite.get(key)!.push(ts);
      });

      const siteData: SiteTimesheets[] = Array.from(groupedBySite.entries()).map(([, timesheets]) => {
        const site = timesheets[0]?.site;
        if (!site) return null;
        const weeklyTimesheets = new Map<string, Timesheet[]>();
        timesheets.forEach(ts => {
          const weekKey = getWeekKey(new Date(ts.clock_in_time));
          if (!weeklyTimesheets.has(weekKey)) weeklyTimesheets.set(weekKey, []);
          weeklyTimesheets.get(weekKey)!.push(ts);
        });
        const weeks: WeekData[] = Array.from(weeklyTimesheets.entries()).map(([weekKey, weekTs]) => {
          const { startDate, endDate } = getWeekDates(weekKey);
          const grossHours = weekTs.reduce((s, ts) => s + getEffectiveHours(ts, now), 0);
          const netHours = weekTs.reduce((s, ts) => {
            const g = getEffectiveHours(ts, now);
            const bm = (ts.breaks || []).reduce((bs, b) => bs + (b.duration_minutes || 0), 0);
            return s + Math.max(0, g - bm / 60);
          }, 0);
          return {
            weekKey, weekStartDate: startDate, weekEndDate: endDate,
            timesheets: weekTs.sort((a, b) => new Date(b.clock_in_time).getTime() - new Date(a.clock_in_time).getTime()),
            grossHours, netHours,
            activeCount: weekTs.filter(ts => ts.status === 'active').length,
            completedCount: weekTs.filter(ts => ts.status === 'completed').length
          };
        });
        weeks.sort((a, b) => b.weekStartDate.getTime() - a.weekStartDate.getTime());
        return { site, weeks, totalHours: weeks.reduce((s, w) => s + w.netHours, 0) };
      }).filter(Boolean) as SiteTimesheets[];

      const uniqueSites = Array.from(new Set(siteData.map(s => s.site))).sort((a, b) => a.name.localeCompare(b.name));
      setAllSites(uniqueSites);
      setSiteTimesheets(siteData);
    } catch (error) {
      console.error('Error fetching timesheets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (timesheetId: string) => {
    if (!confirm('Are you sure you want to delete this timesheet?')) return;
    try {
      const { error } = await supabase.from('timesheets').delete().eq('id', timesheetId);
      if (error) throw error;
      await fetchTimesheets();
    } catch (error) {
      console.error('Error deleting timesheet:', error);
      alert('Failed to delete timesheet');
    }
  };

  const toggleWeekExpansion = (weekId: string) => {
    const newExpanded = new Set(expandedWeeks);
    if (newExpanded.has(weekId)) newExpanded.delete(weekId);
    else newExpanded.add(weekId);
    setExpandedWeeks(newExpanded);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredWorkerTimesheets = workerTimesheets.map(workerData => {
    if (filterWorker && workerData.worker.id !== filterWorker) return { ...workerData, weeks: [] };
    const filteredWeeks = workerData.weeks.map(week => ({
      ...week,
      timesheets: week.timesheets.filter(ts => {
        const matchesSite = !filterSite || ts.site_id === filterSite || ts.archived_site_id === filterSite;
        const matchesSearch = ts.worker?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (ts.site?.name || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = !filterStatus || ts.status === filterStatus;
        return matchesSite && matchesSearch && matchesStatus;
      })
    })).filter(week => week.timesheets.length > 0);
    return { ...workerData, weeks: filteredWeeks };
  }).filter(w => w.weeks.length > 0 || w.worker.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const filteredSiteTimesheets = siteTimesheets.map(siteData => {
    if (filterSite && siteData.site.id !== filterSite) return { ...siteData, weeks: [] };
    const filteredWeeks = siteData.weeks.map(week => ({
      ...week,
      timesheets: week.timesheets.filter(ts => {
        const matchesWorker = !filterWorker || ts.worker_id === filterWorker;
        const matchesSearch = ts.worker?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (ts.site?.name || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = !filterStatus || ts.status === filterStatus;
        return matchesWorker && matchesSearch && matchesStatus;
      })
    })).filter(week => week.timesheets.length > 0);
    return { ...siteData, weeks: filteredWeeks };
  }).filter(s => s.weeks.length > 0 || s.site.name.toLowerCase().includes(searchTerm.toLowerCase()));

  useEffect(() => {
    fetchTimesheets();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Timesheets</h1>
          <p className="text-gray-500 text-sm mt-0.5">Track and manage worker time records</p>
        </div>
        <button
          onClick={() => { setEditingTimesheet(null); setShowModal(true); }}
          className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors flex items-center space-x-2 text-sm font-medium"
        >
          <Plus className="h-4 w-4" />
          <span>Add Timesheet</span>
        </button>
      </div>

      {/* View mode + filters bar */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4 flex flex-wrap items-center gap-3">
        {/* View mode tabs */}
        <div className="flex bg-gray-100 rounded-lg p-1 text-sm">
          {([['grid', 'Weekly Grid'], ['worker', 'By Worker'], ['site', 'By Site']] as const).map(([mode, label]) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`px-3 py-1.5 rounded-md font-medium transition-colors ${
                viewMode === mode ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="w-px h-6 bg-gray-200 hidden sm:block" />

        {/* Search */}
        <div className="relative flex-1 min-w-36">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Search workers or sites..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent w-full text-sm"
          />
        </div>

        {/* Status filter */}
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 text-sm bg-white"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
        </select>

        {/* Worker filter */}
        <select
          value={filterWorker}
          onChange={(e) => setFilterWorker(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 text-sm bg-white"
        >
          <option value="">All Workers</option>
          {allWorkers.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>

        {/* Site filter */}
        <select
          value={filterSite}
          onChange={(e) => setFilterSite(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 text-sm bg-white"
        >
          <option value="">All Sites</option>
          {allSites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>

        {(searchTerm || filterStatus || filterWorker || filterSite) && (
          <button
            onClick={() => { setSearchTerm(''); setFilterStatus(''); setFilterWorker(''); setFilterSite(''); }}
            className="text-xs text-gray-500 hover:text-gray-700 underline"
          >
            Clear
          </button>
        )}
      </div>

      {/* ── WEEKLY GRID VIEW ── */}
      {viewMode === 'grid' && (
        <>
          {/* Week navigator */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <button onClick={() => setWeekOffset(o => o - 1)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors">
                <ChevronLeft size={16} />
              </button>
              <div className="text-center">
                <p className="text-sm font-semibold text-gray-900">{weekLabel}</p>
                <p className="text-xs text-gray-400 mt-0.5">Work week (Fri – Thu)</p>
              </div>
              <button onClick={() => setWeekOffset(o => o + 1)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors">
                <ChevronRight size={16} />
              </button>
            </div>
            {!isCurrentWeek && (
              <button
                onClick={() => setWeekOffset(0)}
                className="text-xs font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-100 px-3 py-1.5 rounded-lg transition-colors"
              >
                Current week
              </button>
            )}
            <div className="flex items-center gap-4 text-sm ml-auto">
              <div className="text-center">
                <p className="font-bold text-gray-900">{formatDuration(grandNet)}</p>
                <p className="text-xs text-gray-400">Total net</p>
              </div>
              {grandBreak > 0 && (
                <div className="text-center">
                  <p className="font-bold text-amber-600">{formatDuration(grandBreak)}</p>
                  <p className="text-xs text-gray-400">Total break</p>
                </div>
              )}
              <div className="text-center">
                <p className="font-bold text-gray-500">{gridRows.length}</p>
                <p className="text-xs text-gray-400">Employees</p>
              </div>
            </div>
          </div>

          {/* Table */}
          {gridRows.length > 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-50/80 border-b border-gray-100">
                      <th className="sticky left-0 z-10 bg-gray-50/80 px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-44 border-r border-gray-100">
                        Employee
                      </th>
                      {days.map((d, i) => {
                        const dk = dayKeys[i];
                        const isToday = dk === todayKey;
                        return (
                          <th key={dk} className={`px-3 py-3 text-center min-w-28 border-r border-gray-100/60 last:border-r-0 ${isToday ? 'bg-blue-50/60' : ''}`}>
                            <p className={`text-xs font-semibold uppercase tracking-wider ${isToday ? 'text-blue-600' : 'text-gray-500'}`}>
                              {DAY_LABELS[i]}
                            </p>
                            <p className={`text-xs mt-0.5 font-normal ${isToday ? 'text-blue-500' : 'text-gray-400'}`}>
                              {d.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' })}
                            </p>
                          </th>
                        );
                      })}
                      <th className="px-4 py-3 text-center min-w-32 bg-gray-100/60 border-l border-gray-200">
                        <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Total</p>
                        <p className="text-xs font-normal text-gray-400 mt-0.5">Net hrs</p>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {gridRows.map((row) => (
                      <tr key={row.profile.id} className="hover:bg-gray-50/40 transition-colors group">
                        <td className="sticky left-0 z-10 bg-white group-hover:bg-gray-50/40 px-5 py-3.5 border-r border-gray-100 transition-colors">
                          <div className="flex items-center gap-2.5">
                            <Avatar name={row.profile.name} size="sm" />
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-900 truncate">{row.profile.name}</p>
                              <p className="text-xs text-gray-400 capitalize">{row.profile.role?.replace(/_/g, ' ')}</p>
                            </div>
                          </div>
                        </td>
                        {dayKeys.map((dk, i) => {
                          const dt = row.days[dk];
                          const isToday = dk === todayKey;
                          const hasHours = dt.gross > 0;
                          return (
                            <td
                              key={dk}
                              onClick={() => setCellDetailPanel({ workerId: row.profile.id, workerName: row.profile.name, dayKey: dk })}
                              className={`px-3 py-3.5 text-center border-r border-gray-100/60 last:border-r-0 align-top cursor-pointer transition-colors
                                ${isToday ? 'bg-blue-50/30 hover:bg-blue-100/50' : 'hover:bg-orange-50/60'}
                                ${hasHours ? '' : 'group/cell'}
                                ${dt.hasAmendment ? 'ring-1 ring-inset ring-amber-300 bg-amber-50/40' : ''}`}
                              title={hasHours ? 'Click to edit' : 'Click to add timesheet'}
                            >
                              {hasHours ? (
                                <div>
                                  {dt.hasAmendment && (
                                    <div className="flex items-center justify-center gap-0.5 mb-1">
                                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                                        <PenLine size={8} />
                                        Amended
                                      </span>
                                    </div>
                                  )}
                                  <p className="text-sm font-semibold text-gray-900">{formatDuration(dt.net)}</p>
                                  {dt.breakTime > 0 && (
                                    <p className="text-xs text-amber-500 mt-0.5 flex items-center justify-center gap-0.5">
                                      <Coffee size={9} /> {formatDuration(dt.breakTime)}
                                    </p>
                                  )}
                                  {dt.breakTime > 0 && (
                                    <p className="text-xs text-gray-400 mt-0.5">{formatDuration(dt.gross)} gross</p>
                                  )}
                                  <p className="text-xs text-gray-400 mt-1 flex items-center justify-center gap-0.5">
                                    <Clock size={9} className="flex-shrink-0" />
                                    {dt.firstIn ? formatTime(dt.firstIn) : '—'}
                                    {' – '}
                                    {dt.lastOut ? formatTime(dt.lastOut) : <span className="text-emerald-500 font-medium">on site</span>}
                                  </p>
                                  <p className="text-xs text-blue-400 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">Edit</p>
                                </div>
                              ) : (
                                <span className="text-gray-200 text-xl group-hover/cell:text-gray-300 transition-colors leading-none">+</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-4 py-3.5 text-center bg-gray-50/60 border-l border-gray-200 align-top">
                          <p className="text-sm font-bold text-gray-900">{formatDuration(row.weekNet)}</p>
                          {row.weekBreak > 0 && (
                            <p className="text-xs text-amber-500 mt-0.5 flex items-center justify-center gap-0.5">
                              <Coffee size={9} /> {formatDuration(row.weekBreak)}
                            </p>
                          )}
                          <p className="text-xs text-gray-400 mt-0.5">{formatDuration(row.weekGross)} gross</p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-200 bg-gray-50/80">
                      <td className="sticky left-0 z-10 bg-gray-50/80 px-5 py-3.5 border-r border-gray-200">
                        <p className="text-xs font-bold text-gray-600 uppercase tracking-wider">Totals</p>
                      </td>
                      {dayKeys.map((dk) => {
                        const dayGross = gridRows.reduce((a, r) => a + r.days[dk].gross, 0);
                        const dayBreak = gridRows.reduce((a, r) => a + r.days[dk].breakTime, 0);
                        const dayNet = Math.max(0, dayGross - dayBreak);
                        const isToday = dk === todayKey;
                        return (
                          <td key={dk} className={`px-3 py-3.5 text-center border-r border-gray-200/60 last:border-r-0 ${isToday ? 'bg-blue-50/40' : ''}`}>
                            {dayNet > 0 ? (
                              <div>
                                <p className="text-sm font-bold text-gray-800">{formatDuration(dayNet)}</p>
                                {dayBreak > 0 && (
                                  <p className="text-xs text-amber-500 mt-0.5">{formatDuration(dayBreak)} break</p>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-300 text-sm">—</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="px-4 py-3.5 text-center bg-gray-100/80 border-l border-gray-200">
                        <p className="text-sm font-bold text-gray-900">{formatDuration(grandNet)}</p>
                        {grandBreak > 0 && (
                          <p className="text-xs text-amber-500 mt-0.5">{formatDuration(grandBreak)} break</p>
                        )}
                        <p className="text-xs text-gray-400 mt-0.5">{formatDuration(grandGross)} gross</p>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
              <Package className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-600">No timesheets for this week</p>
              <p className="text-xs text-gray-400 mt-1">Navigate to another week or add a timesheet</p>
            </div>
          )}
        </>
      )}

      {/* ── BY WORKER / BY SITE LIST VIEWS ── */}
      {(viewMode === 'worker' || viewMode === 'site') && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {/* Header row */}
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/60 flex items-center justify-between flex-wrap gap-3">
            <h3 className="text-sm font-semibold text-gray-700">
              {viewMode === 'worker' ? 'Timesheets by Worker & Week' : 'Timesheets by Site & Week'}
            </h3>
            <div className="flex items-center gap-5 text-sm">
              <div className="text-center">
                <p className="font-semibold text-gray-900">
                  {(viewMode === 'worker' ? filteredWorkerTimesheets : filteredSiteTimesheets).reduce(
                    (s, x) => s + x.weeks.reduce((ws, w) => ws + w.timesheets.reduce((ts, t) => {
                      const g = getEffectiveHours(t, now);
                      const bm = (t.breaks || []).reduce((bs, b) => bs + (b.duration_minutes || 0), 0);
                      return ts + Math.max(0, g - bm / 60);
                    }, 0), 0), 0).toFixed(1)}h
                </p>
                <p className="text-xs text-gray-400">Total hours</p>
              </div>
              <div className="text-center">
                <p className="font-semibold text-gray-900">
                  {viewMode === 'worker' ? filteredWorkerTimesheets.length : filteredSiteTimesheets.length}
                </p>
                <p className="text-xs text-gray-400">{viewMode === 'worker' ? 'Workers' : 'Sites'}</p>
              </div>
            </div>
          </div>

          <div className="divide-y divide-gray-50">
            {viewMode === 'site' ? (
              filteredSiteTimesheets.map(siteData =>
                siteData.weeks.map(week => {
                  const weekId = `${siteData.site.id}-${week.weekKey}`;
                  const isExpanded = expandedWeeks.has(weekId);
                  return (
                    <div key={weekId}>
                      <div className="px-6 py-4 hover:bg-gray-50/60 cursor-pointer transition-colors" onClick={() => toggleWeekExpansion(weekId)}>
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3 min-w-0">
                            {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                            <div className="h-9 w-9 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                              <MapPin className="h-4 w-4 text-orange-600" />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-semibold text-gray-900 truncate">{siteData.site.name}</p>
                                {siteData.timesheets.some(ts => ts.archived_site_id && !ts.site_id) && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-200 text-gray-600 border border-gray-300">
                                    Archived
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-400 truncate">Week ending {week.weekEndDate.toLocaleDateString('en-AU')} (Fri–Thu)</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 flex-shrink-0">
                            <div className="flex items-center gap-3 text-xs text-gray-500">
                              <span><span className="font-semibold text-green-700">{week.activeCount}</span> active</span>
                              <span><span className="font-semibold text-blue-700">{week.completedCount}</span> completed</span>
                              <span className="font-semibold text-gray-800">{formatDuration(week.netHours)}</span>
                            </div>
                            <Link to={`/sites/${siteData.site.id}`} className="text-orange-600 hover:text-orange-700 text-xs font-medium" onClick={e => e.stopPropagation()}>
                              View Site
                            </Link>
                          </div>
                        </div>
                      </div>
                      {isExpanded && <ExpandedTimesheets timesheets={week.timesheets} week={week} now={now} onEdit={ts => { setEditingTimesheet(ts); setShowModal(true); }} onDelete={handleDelete} getStatusColor={getStatusColor} />}
                    </div>
                  );
                })
              )
            ) : (
              filteredWorkerTimesheets.map(workerData =>
                workerData.weeks.map(week => {
                  const weekId = `${workerData.worker.id}-${week.weekKey}`;
                  const isExpanded = expandedWeeks.has(weekId);
                  return (
                    <div key={weekId}>
                      <div className="px-6 py-4 hover:bg-gray-50/60 cursor-pointer transition-colors" onClick={() => toggleWeekExpansion(weekId)}>
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3 min-w-0">
                            {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                            <Avatar name={workerData.worker.name} size="sm" />
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-900 truncate">{workerData.worker.name}</p>
                              <p className="text-xs text-gray-400 truncate capitalize">{workerData.worker.role?.replace(/_/g, ' ')} · Week ending {week.weekEndDate.toLocaleDateString('en-AU')}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 flex-shrink-0">
                            <div className="flex items-center gap-3 text-xs text-gray-500">
                              <span><span className="font-semibold text-green-700">{week.activeCount}</span> active</span>
                              <span><span className="font-semibold text-blue-700">{week.completedCount}</span> completed</span>
                              <span className="font-semibold text-gray-800">{formatDuration(week.netHours)}</span>
                            </div>
                            <Link to={`/workers/${workerData.worker.id}`} className="text-orange-600 hover:text-orange-700 text-xs font-medium" onClick={e => e.stopPropagation()}>
                              View Worker
                            </Link>
                          </div>
                        </div>
                      </div>
                      {isExpanded && <ExpandedTimesheets timesheets={week.timesheets} week={week} now={now} onEdit={ts => { setEditingTimesheet(ts); setShowModal(true); }} onDelete={handleDelete} getStatusColor={getStatusColor} />}
                    </div>
                  );
                })
              )
            )}
          </div>

          {(viewMode === 'site' ? filteredSiteTimesheets : filteredWorkerTimesheets).length === 0 && (
            <div className="p-12 text-center">
              <Clock className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-600">
                {searchTerm || filterStatus || filterWorker || filterSite ? 'No matching timesheets' : 'No timesheets recorded'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Timesheet Form Modal — TimesheetForm renders its own modal overlay */}
      {showModal && (
        <TimesheetForm
          timesheet={editingTimesheet}
          onSubmit={async (data) => {
            setSubmitting(true);
            try {
              if (editingTimesheet?.id) {
                const { error } = await supabase.from('timesheets').update({
                  ...data,
                  is_amended: true,
                  amended_at: new Date().toISOString(),
                }).eq('id', editingTimesheet.id);
                if (error) throw error;
              } else {
                const { error } = await supabase.from('timesheets').insert([data]);
                if (error) throw error;
              }
              await fetchTimesheets();
              setShowModal(false);
              setEditingTimesheet(null);
            } catch (error) {
              console.error('Error saving timesheet:', error);
              alert('Failed to save timesheet');
            } finally {
              setSubmitting(false);
            }
          }}
          onClose={() => { setShowModal(false); setEditingTimesheet(null); }}
          loading={submitting}
        />
      )}

      {/* Cell detail panel — shows timesheet(s) for a clicked grid cell */}
      {cellDetailPanel && (
        <CellDetailPanel
          workerId={cellDetailPanel.workerId}
          workerName={cellDetailPanel.workerName}
          dayKey={cellDetailPanel.dayKey}
          timesheets={getTimesheetsForCell(cellDetailPanel.workerId, cellDetailPanel.dayKey)}
          now={now}
          onClose={() => setCellDetailPanel(null)}
          onEdit={(ts) => {
            setCellDetailPanel(null);
            setEditingTimesheet(ts);
            setShowModal(true);
          }}
          onAdd={(workerId) => {
            setCellDetailPanel(null);
            // Pre-fill with worker + midnight of selected day (AEST)
            setEditingTimesheet({ worker_id: workerId, clock_in_time: `${cellDetailPanel.dayKey}T07:00:00+10:00` } as Timesheet);
            setShowModal(true);
          }}
          onDelete={async (id) => {
            await handleDelete(id);
            setCellDetailPanel(null);
          }}
          getStatusColor={getStatusColor}
        />
      )}
    </div>
  );
};

// Extracted expanded detail panel to keep the main component readable
function ExpandedTimesheets({
  timesheets, week, now, onEdit, onDelete, getStatusColor
}: {
  timesheets: Timesheet[];
  week: WeekData;
  now: number;
  onEdit: (ts: Timesheet) => void;
  onDelete: (id: string) => void;
  getStatusColor: (s: string) => string;
}) {
  return (
    <div className="bg-gray-50/60 border-t border-gray-100 px-6 py-4 space-y-3">
      {/* Week summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-1">
        {[
          { label: 'Gross', val: `${week.grossHours.toFixed(1)}h`, color: 'text-gray-800' },
          { label: 'Net', val: `${week.netHours.toFixed(1)}h`, color: 'text-gray-800' },
          { label: 'Days', val: `${week.timesheets.length}`, color: 'text-gray-800' },
          { label: 'Active', val: `${week.activeCount}`, color: 'text-green-700' },
        ].map(item => (
          <div key={item.label} className="bg-white rounded-xl border border-gray-100 px-4 py-2.5 text-center">
            <p className={`text-base font-bold ${item.color}`}>{item.val}</p>
            <p className="text-xs text-gray-400 mt-0.5">{item.label}</p>
          </div>
        ))}
      </div>

      {timesheets.map(ts => {
        const gross = getEffectiveHours(ts, now);
        const breakMins = (ts.breaks || []).reduce((s, b) => s + (b.duration_minutes || 0), 0);
        const net = Math.max(0, gross - breakMins / 60);
        return (
          <div key={ts.id} className={`bg-white rounded-xl border p-4 ${ts.is_amended ? 'border-amber-300 bg-amber-50/30' : 'border-gray-100'}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-gray-900">{ts.site?.name || ts.worker?.name}</p>
                  {ts.is_amended && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                      <PenLine size={10} /> Amended
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-400">{ts.site?.address || ts.worker?.role?.replace(/_/g, ' ')}</p>
                {ts.is_amended && ts.amended_at && (
                  <p className="text-xs text-amber-600 mt-0.5">
                    Edited {new Date(ts.amended_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true })}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 text-center flex-shrink-0">
                <p className="text-sm font-semibold text-gray-900">
                  {new Date(ts.clock_in_time).toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short', timeZone: 'Australia/Sydney' })}
                </p>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(ts.status)}`}>
                  {ts.status.charAt(0).toUpperCase() + ts.status.slice(1)}
                </span>
                <button onClick={() => onEdit(ts)} className="text-blue-600 hover:text-blue-800 text-xs flex items-center gap-1">
                  <Edit className="h-3.5 w-3.5" /> Edit
                </button>
                <button onClick={() => onDelete(ts.id)} className="text-red-500 hover:text-red-700 text-xs">Delete</button>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-2 text-xs mb-2">
              <div className="bg-gray-50 rounded-lg p-2 text-center">
                <p className="font-medium text-gray-500">Clock In</p>
                <p className="font-semibold text-gray-800 mt-0.5">{new Date(ts.clock_in_time).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Sydney' })}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2 text-center">
                <p className="font-medium text-gray-500">Clock Out</p>
                <p className={`font-semibold mt-0.5 ${ts.clock_out_time ? 'text-gray-800' : 'text-emerald-600'}`}>
                  {ts.clock_out_time ? new Date(ts.clock_out_time).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Sydney' }) : 'On site'}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2 text-center">
                <p className="font-medium text-gray-500">Gross</p>
                <p className="font-semibold text-gray-800 mt-0.5">{gross.toFixed(1)}h</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2 text-center">
                <p className="font-medium text-gray-500">Net</p>
                <p className="font-semibold text-gray-800 mt-0.5">{net.toFixed(1)}h</p>
              </div>
            </div>

            {ts.breaks && ts.breaks.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <Coffee className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                {ts.breaks.map(b => (
                  <span key={b.id} className="px-2 py-0.5 rounded text-xs bg-amber-50 text-amber-700">
                    {b.break_type.charAt(0).toUpperCase() + b.break_type.slice(1)}{b.duration_minutes ? ` (${b.duration_minutes}min)` : ''}
                  </span>
                ))}
              </div>
            )}

            {(ts.hourly_rate_used || ts.total_pay) && (
              <div className="mt-2 pt-2 border-t border-gray-50 flex items-center gap-4 text-xs text-gray-600">
                {ts.hourly_rate_used && <span>Rate: <strong className="text-gray-800">${ts.hourly_rate_used.toFixed(2)}/hr</strong></span>}
                {ts.overtime_rate_used && <span>OT: <strong className="text-gray-800">${ts.overtime_rate_used.toFixed(2)}/hr</strong></span>}
                {ts.regular_hours && <span>Reg: <strong className="text-gray-800">{ts.regular_hours.toFixed(1)}h</strong></span>}
                {ts.overtime_hours && <span>OT hrs: <strong className="text-gray-800">{ts.overtime_hours.toFixed(1)}h</strong></span>}
                {ts.total_pay && <span className="ml-auto font-bold text-green-700">Total: ${ts.total_pay.toFixed(2)}</span>}
              </div>
            )}

            {(ts.clock_in_latitude || ts.clock_out_latitude) && (
              <div className="mt-2 pt-2 border-t border-gray-50 flex items-center gap-4 text-xs text-gray-500">
                <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                {ts.clock_in_latitude && ts.clock_in_longitude && (
                  <button onClick={() => window.open(`https://www.google.com/maps?q=${ts.clock_in_latitude},${ts.clock_in_longitude}`, '_blank')} className="text-blue-600 hover:underline">
                    In: {ts.clock_in_latitude.toFixed(4)}, {ts.clock_in_longitude.toFixed(4)}
                  </button>
                )}
                {ts.clock_out_latitude && ts.clock_out_longitude && (
                  <button onClick={() => window.open(`https://www.google.com/maps?q=${ts.clock_out_latitude},${ts.clock_out_longitude}`, '_blank')} className="text-blue-600 hover:underline">
                    Out: {ts.clock_out_latitude.toFixed(4)}, {ts.clock_out_longitude.toFixed(4)}
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Modal panel showing all timesheets for a specific worker+day, with edit/delete/add actions
function CellDetailPanel({
  workerId, workerName, dayKey, timesheets, now, onClose, onEdit, onAdd, onDelete, getStatusColor
}: {
  workerId: string;
  workerName: string;
  dayKey: string;
  timesheets: Timesheet[];
  now: number;
  onClose: () => void;
  onEdit: (ts: Timesheet) => void;
  onAdd: (workerId: string) => void;
  onDelete: (id: string) => void;
  getStatusColor: (s: string) => string;
}) {
  const [date] = dayKey.split('T');
  const displayDate = new Date(`${date}T12:00:00`).toLocaleDateString('en-AU', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-base font-bold text-gray-900">{workerName}</h3>
            <p className="text-xs text-gray-400 mt-0.5">{displayDate}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Timesheet list */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {timesheets.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="h-8 w-8 text-gray-200 mx-auto mb-2" />
              <p className="text-sm text-gray-400">No timesheets on this day</p>
            </div>
          ) : (
            timesheets.map(ts => {
              const gross = getEffectiveHours(ts, now);
              const breakMins = (ts.breaks || []).reduce((s, b) => s + (b.duration_minutes || 0), 0);
              const net = Math.max(0, gross - breakMins / 60);
              return (
                <div key={ts.id} className={`border rounded-xl p-4 ${ts.is_amended ? 'border-amber-300 bg-amber-50/40' : 'border-gray-100 bg-gray-50/50'}`}>
                  {/* Top row: site + status + actions */}
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-gray-900">{ts.site?.name || '—'}</p>
                        {ts.is_amended && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                            <PenLine size={10} /> Amended
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400">{ts.site?.address || ''}</p>
                      {ts.is_amended && ts.amended_at && (
                        <p className="text-xs text-amber-600 mt-0.5">
                          Edited {new Date(ts.amended_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true })}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(ts.status)}`}>
                        {ts.status.charAt(0).toUpperCase() + ts.status.slice(1)}
                      </span>
                    </div>
                  </div>

                  {/* Time grid */}
                  <div className="grid grid-cols-4 gap-2 text-xs mb-3">
                    <div className="bg-white border border-gray-100 rounded-lg p-2 text-center">
                      <p className="text-gray-400 font-medium">In</p>
                      <p className="font-semibold text-gray-800 mt-0.5">
                        {new Date(ts.clock_in_time).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Sydney', hour12: false })}
                      </p>
                    </div>
                    <div className="bg-white border border-gray-100 rounded-lg p-2 text-center">
                      <p className="text-gray-400 font-medium">Out</p>
                      <p className={`font-semibold mt-0.5 ${ts.clock_out_time ? 'text-gray-800' : 'text-emerald-600'}`}>
                        {ts.clock_out_time
                          ? new Date(ts.clock_out_time).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Sydney', hour12: false })
                          : 'On site'}
                      </p>
                    </div>
                    <div className="bg-white border border-gray-100 rounded-lg p-2 text-center">
                      <p className="text-gray-400 font-medium">Gross</p>
                      <p className="font-semibold text-gray-800 mt-0.5">{gross.toFixed(1)}h</p>
                    </div>
                    <div className="bg-white border border-gray-100 rounded-lg p-2 text-center">
                      <p className="text-gray-400 font-medium">Net</p>
                      <p className="font-semibold text-gray-800 mt-0.5">{net.toFixed(1)}h</p>
                    </div>
                  </div>

                  {/* Breaks */}
                  {ts.breaks && ts.breaks.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap mb-3">
                      <Coffee className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                      {ts.breaks.map(b => (
                        <span key={b.id} className="px-2 py-0.5 rounded text-xs bg-amber-50 text-amber-700">
                          {b.break_type.charAt(0).toUpperCase() + b.break_type.slice(1)}{b.duration_minutes ? ` (${b.duration_minutes}min)` : ''}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => onEdit(ts)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-orange-600 text-white text-xs font-semibold rounded-lg hover:bg-orange-700 transition-colors"
                    >
                      <Edit className="h-3.5 w-3.5" /> Edit Timesheet
                    </button>
                    <button
                      onClick={() => {
                        if (confirm('Delete this timesheet?')) onDelete(ts.id);
                      }}
                      className="px-3 py-2 border border-red-200 text-red-500 text-xs font-medium rounded-lg hover:bg-red-50 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer: add new */}
        <div className="px-6 py-4 border-t border-gray-100">
          <button
            onClick={() => onAdd(workerId)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-200 text-gray-400 text-sm font-medium rounded-xl hover:border-orange-300 hover:text-orange-500 transition-colors"
          >
            <Plus className="h-4 w-4" /> Add timesheet for this day
          </button>
        </div>
      </div>
    </div>
  );
}
